/**
 * Canonical driver vocabulary for Forecast V3 — the ONE place user-facing
 * driver wording lives. Persisted driverType keys (e.g. '$ / ORN') never
 * change; only how they are shown.
 *
 * Rules:
 *   - per occupied room  => "$POR"   (never ORN / "occupied room night")
 *   - per available room => "$PAR"   (never ARN)
 *   - the stat rows are "Occupied Rooms" / "Available Rooms"
 *   - RevPAR is spelled "RevPAR"
 *   - budget-based variants prefix "Bud"/"Budgeted"; last-month "LM"; last-year "LY"
 * Backend helper text (drivers/*.js getHelperText) and driverEligibility
 * reasons follow the same rules; azfApi/tests/driverCatalogCoverage.test.js
 * keeps the picker in sync with the registry.
 */
export const TERMS = Object.freeze({
  POR: '$POR',
  PAR: '$PAR',
  OCCUPIED_ROOMS: 'Occupied Rooms',
  AVAILABLE_ROOMS: 'Available Rooms',
  REVPAR: 'RevPAR',
});

/** label = full name (pickers, panels); short = badge/chip text (grid, bulk edit, AI review). */
export const DRIVER_LABELS: Record<string, { label: string; short: string }> = {
  'Manual': { label: 'Manual Entry', short: 'Manual' },
  'Static Entry': { label: 'Static Entry', short: 'Static' },
  'Budget': { label: 'Budget', short: 'Budget' },
  '$ / ORN': { label: '$POR — $ per occupied room', short: '$POR' },
  '$ / ARN': { label: '$PAR — $ per available room', short: '$PAR' },
  'REVPAR Projection': { label: 'RevPAR Projection (retired)', short: 'RevPAR (retired)' }, // legacy rows only; not assignable
  'Occupancy Forecast': { label: 'Occupancy Forecast', short: 'Occ Fcst' },
  '$ LM': { label: '$ Last Month', short: '$ LM' },
  '$ / ORN LM': { label: '$POR Last Month', short: '$POR LM' },
  '$ / ARN LM': { label: '$PAR Last Month', short: '$PAR LM' },
  'OTB Forecast': { label: 'OTB Forecast', short: 'OTB' },
  'RMS Forecast': { label: 'RMS Forecast', short: 'RMS' },
  'Room Forecast': { label: 'Room Forecast', short: 'Room Fcst' },
  '% of Revenue': { label: '% of Total Revenue', short: '% Total Rev' },
  '% of Selected Revenue': { label: '% of Selected Revenue', short: '% Sel Rev' },
  'LY Same Month': { label: 'Last Year Same Month', short: 'LY Same Month' },
  'YoY Growth %': { label: 'YoY Growth %', short: 'YoY %' },
  '$ / ORN LY': { label: '$POR Last Year', short: '$POR LY' },
  '$ / ARN LY': { label: '$PAR Last Year', short: '$PAR LY' },
  'Seasonal Budget Spread': { label: 'Seasonal Budget Spread', short: 'Seasonal Bud' },
  '% of Selected Revenue (Budgeted)': { label: '% of Selected Revenue (Budgeted)', short: 'Bud % Sel Rev' },
  '% of Total Revenue (Budgeted)': { label: '% of Total Revenue (Budgeted)', short: 'Bud % Total Rev' },
  'Budgeted $POR': { label: 'Budgeted $POR', short: 'Bud $POR' },
  'Budgeted $PAR': { label: 'Budgeted $PAR', short: 'Bud $PAR' },
  '% of Account': { label: '% of Account', short: '% Acct' },
  'HPOR': { label: 'HPOR (hours per occupied room)', short: 'HPOR' },
  'DPOR': { label: '$POR Projection (legacy DPOR)', short: '$POR Proj' },
  '$POR Projection': { label: '$POR Projection', short: '$POR Proj' },
  'Avg Rate x Hours': { label: 'Avg Rate × Hours', short: 'Rate × Hrs' },
  'Labor Config': { label: 'Labor Config', short: 'Labor Config' },
  'Zero Base': { label: 'Zero Base', short: 'Zero Base' },
};

export const driverLabel = (driverType: string | null | undefined): string =>
  (driverType && DRIVER_LABELS[driverType]?.label) || driverType || 'Manual Entry';
export const driverShortLabel = (driverType: string | null | undefined): string =>
  (driverType && DRIVER_LABELS[driverType]?.short) || driverType || 'Manual';

export const LY_BUDGET_DRIVER_BASES: Record<string, string> = {'LY Published Budget': 'Budget', 'LY Published Budget $POR': 'Budgeted $POR', 'LY Published Budget $PAR': 'Budgeted $PAR', '% of Total Revenue (LY Budget)': '% of Total Revenue (Budgeted)', '% of Selected Revenue (LY Budget)': '% of Selected Revenue (Budgeted)'};

export function isSelectedRevenueDriver(type: string): boolean {
  return ['% of Selected Revenue', '% of Selected Revenue (Budgeted)'].includes(LY_BUDGET_DRIVER_BASES[type] || type);
}
